import { getItemByLabelFactory, getItemLabelsFactory } from './_common'
import type { ISelectItem } from '~/types/models'

/**
 * ⚠ SERVER-OWNED ids, matching `CaseStatus` in the api server's
 * resources/api/entities/models/select_items.py and the `casestatuses` rows it
 * seeds. A write sends the id and the server resolves the FK from it WITHOUT
 * checking the label, so a number that drifts from the server's is not an error
 * -- it silently files the case under a different status.
 */
export enum CaseStatusIds {
  FROM_EMAIL_NEEDS_REVIEW = 1,
  PENDING_CUST_INFO = 2,
  PENDING_DISPATCH = 3,
  ACTIVE_IN_PROGRESS = 4,
  ACTIVE_OTHER = 5,
  ESCALATED = 6,
  PAUSED = 7,
  CANCELED = 8,
  RESOLVED = 9,
  // Set ONLY by a merge (POST /api/cases/{id}/merges, cynque-api-server #1658):
  // the case was folded into another, which `mergedInto` names. A CHECK ties the
  // two together, so a PATCH to or from Merged is a 409 -- never offer it as a
  // choice (see getCaseStatiiForSelect).
  MERGED = 10,
}

/**
 * The statuses a case is FINISHED in. Everything else is open.
 *
 * ⚠ `PAUSED` is deliberately OPEN. A paused case is coming back -- work stopped,
 * it did not end -- so hiding it from "All Open" would lose it. The line is
 * drawn at "is this case done", not at "can I act on it today".
 *
 * Keyed by ID rather than label: ids are the stable, server-owned identity,
 * while labels are display text that gets reworded. See `isOpenCaseStatus`.
 */
export const TERMINAL_CASE_STATUS_IDS: readonly CaseStatusIds[] = [
  CaseStatusIds.CANCELED,
  CaseStatusIds.RESOLVED,
  // A merged case lives on only as a pointer to its survivor; its mail, notes
  // and documents have moved there. Terminal, so the Cases list's default
  // "All Open" never shows it.
  CaseStatusIds.MERGED,
]

/** True for any status that is not terminal — see TERMINAL_CASE_STATUS_IDS. */
export function isOpenCaseStatus(statusId: number | undefined): boolean {
  return !TERMINAL_CASE_STATUS_IDS.includes(statusId as CaseStatusIds)
}

export function getCaseStatii(): ISelectItem[] {
  return [
    {
      id: CaseStatusIds.FROM_EMAIL_NEEDS_REVIEW,
      label: 'From Email\nNeeds Review',
    },
    { id: CaseStatusIds.PENDING_CUST_INFO, label: 'Pending Cust Info' },
    { id: CaseStatusIds.PENDING_DISPATCH, label: 'Pending Dispatch' },
    { id: CaseStatusIds.ACTIVE_IN_PROGRESS, label: 'Active (In Progress)' },
    { id: CaseStatusIds.ACTIVE_OTHER, label: 'Active (Other)' },
    { id: CaseStatusIds.ESCALATED, label: 'Escalated' },
    { id: CaseStatusIds.PAUSED, label: 'Paused' },
    { id: CaseStatusIds.CANCELED, label: 'Canceled' },
    { id: CaseStatusIds.RESOLVED, label: 'Resolved' },
    { id: CaseStatusIds.MERGED, label: 'Merged' },
  ]
}

/**
 * The case form's Status choices: every status, with Merged present but
 * DISABLED. Present, so a merged case's own status still titles the select
 * (Vuetify titles it from the id-matched item); disabled, because only a merge
 * can set it and the server refuses a PATCH to it with a 409.
 */
export function getCaseStatiiForSelect(): ISelectItem[] {
  return getCaseStatii().map((status) =>
    status.id === CaseStatusIds.MERGED ? { ...status, disabled: true } : status
  )
}

/** A case folded into another by a merge; `mergedInto` names the survivor. */
export function isMergedCase(theCase: {
  status?: ISelectItem | null
  mergedInto?: string | null
}): boolean {
  return !!theCase?.mergedInto || theCase?.status?.id === CaseStatusIds.MERGED
}

export const getCaseStatusByLabel = getItemByLabelFactory(getCaseStatii)

/**
 * The status labels as `{ name }` filter entries, for the Cases list page's
 * `showForStatus` menu. Mirrors `getOrderStatiiLabels` in orderStatii.ts.
 *
 * ⚠ DERIVED, NOT RETYPED, and that is the point. Both filter paths compare the
 * chosen value to `item.status.label` with `===` (groupByKey's showStatus test
 * and ListPage.filterAllGroups), so a hand-written entry that drifted from the
 * label -- losing the `\n` in 'From Email\nNeeds Review', say -- would not
 * error. It would just match nothing and show an empty list. Taking the strings
 * from the same function that defines them removes that failure mode, and a
 * status added above appears in the filter without a second edit.
 */
export const getCaseStatiiLabels = getItemLabelsFactory(getCaseStatii)
